import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts';

const drawLine = keyframes`
  from {
    stroke-dashoffset: 1000;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const SparklineWrapper = styled.div`
  width: 100%;
  height: ${props => props.$height || 40}px;
  margin-top: 8px;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: 0.3s;
  
  .recharts-area-curve {
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
    animation: ${drawLine} 1.5s ease-out forwards;
    animation-delay: 0.5s;
  }
`;

const TrendBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-top: 8px;
  
  &.trend-up {
    color: #10B981;
    background: rgba(16, 185, 129, 0.1);
  }
  
  &.trend-down {
    color: #EF4444;
    background: rgba(239, 68, 68, 0.1);
  }
  
  &.trend-neutral {
    color: #6B7280;
    background: rgba(107, 114, 128, 0.1);
  }
`;

/**
 * Mini sparkline chart for stat cards
 * Generates mock trend data if not provided
 * If actualValue is 0, shows flat line and 0% trend
 */
const MiniSparkline = ({ 
  data = null, 
  color = '#4ECDC4', 
  height = 40,
  showTrend = true,
  trendValue = null,
  actualValue = null  // Pass the actual stat value to handle 0 cases
}) => {
  // Check if the actual value is 0 or very small
  const isZeroValue = actualValue !== null && actualValue === 0;

  // Generate mock data if not provided
  const chartData = useMemo(() => {
    // If actual value is 0, show a flat line at the bottom (value 0)
    if (isZeroValue) {
      return Array.from({ length: 7 }, (_, i) => ({ value: 0, index: i }));
    }
    
    if (data && data.length > 0) return data;
    
    // Generate realistic-looking random data with values between 20-100
    // This ensures lines appear in upper portion of chart area
    const points = 7;
    const baseValue = 60;
    let lastValue = baseValue;
    
    return Array.from({ length: points }, (_, i) => {
      const change = (Math.random() - 0.4) * 20; // Slight upward bias
      lastValue = Math.max(30, Math.min(100, lastValue + change));
      return { value: lastValue, index: i };
    });
  }, [data, isZeroValue]);

  // Calculate trend - if value is 0, trend is 0
  const trend = useMemo(() => {
    if (isZeroValue) return 0;
    if (trendValue !== null) return trendValue;
    if (chartData.length < 2) return 0;
    
    const firstHalf = chartData.slice(0, Math.floor(chartData.length / 2));
    const secondHalf = chartData.slice(Math.floor(chartData.length / 2));
    
    const firstAvg = firstHalf.reduce((acc, d) => acc + d.value, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((acc, d) => acc + d.value, 0) / secondHalf.length;
    
    if (firstAvg === 0) return 0;
    return Math.round(((secondAvg - firstAvg) / firstAvg) * 100);
  }, [chartData, trendValue, isZeroValue]);

  const trendClass = trend > 0 ? 'trend-up' : trend < 0 ? 'trend-down' : 'trend-neutral';
  const trendIcon = trend > 0 ? '↑' : trend < 0 ? '↓' : '→';

  return (
    <div>
      <SparklineWrapper $height={height}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
            <defs>
              <linearGradient id={`sparklineGradient-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <YAxis domain={[0, 100]} hide={true} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#sparklineGradient-${color.replace('#', '')})`}
              isAnimationActive={false}
              baseValue={0}
            />
          </AreaChart>
        </ResponsiveContainer>
      </SparklineWrapper>
      {showTrend && (
        <TrendBadge className={trendClass}>
          {trendIcon} {Math.abs(trend)}% vs last period
        </TrendBadge>
      )}
    </div>
  );
};

MiniSparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number.isRequired
  })),
  color: PropTypes.string,
  height: PropTypes.number,
  showTrend: PropTypes.bool,
  trendValue: PropTypes.number,
  actualValue: PropTypes.number
};

export default MiniSparkline;
